import { useContext } from 'react'

import type { UseFormOptions } from '@domain-group/react-hooks'
import {
  useForm, useLatestEntity, useThunkDispatch,
} from '@domain-group/react-hooks'

import { launchModal } from '@domain-group/javascript-utils'

import { mergeAndStringifyEntityObjects } from '@functions/entity'

import useSelector from '@hooks/useSelector'

import defaultState, { requiredFields } from '@models/brandGroup'
import type { BrandGroupModel, BrandModel } from '@models/types'

import PageContext from '@contexts/pageContext'

import * as brandGroupActions from '@redux/modules/brandGroup'
import type { AppDispatch } from '@redux/store'
import type { EntityOptions } from '@redux/modules/types'

export const generateUrls = (brandGroup: Partial<BrandGroupModel> = {}) => {
  const { id } = brandGroup

  return {
    brandGroupsIndexUrl: '#/systemManager/brandGroups',
    editBrandGroupRelationsUrl: `#/systemManager/brandGroups/${id}/edit/relations`,
    editBrandGroupUrl: `#/systemManager/brandGroups/${id}/edit`,
  }
}

export type CreateBrandGroupParams = {
  brandGroupParams: Partial<BrandGroupModel>,
  dispatch: AppDispatch,
}

const createBrandGroup = (params: CreateBrandGroupParams) => {
  const { brandGroupParams, dispatch } = params
  const { createBrandGroup: createFn } = brandGroupActions

  const updatedParams = {
    ...brandGroupParams,
  }

  // @ts-expect-error TS2345
  return dispatch(createFn(updatedParams)).then((response) => {
    const { success } = response

    if (success) {
      const { data } = response
      const { id } = data

      const { editBrandGroupUrl } = generateUrls({ id })

      return {
        success,
        data,
        redirectUrl: editBrandGroupUrl,
      }
    }

    return response
  })
}

export type UpdateBrandGroupParams = {
  brandGroup: BrandGroupModel,
  brandGroupParams: Partial<BrandGroupModel>,
  dispatch: AppDispatch,
  requestOptions?: EntityOptions,
}

const updateBrandGroup = (params: UpdateBrandGroupParams) => {
  const {
    brandGroup, brandGroupParams, dispatch, requestOptions,
  } = params
  const { updateBrandGroup: updateFn } = brandGroupActions

  const updatedParams = mergeAndStringifyEntityObjects(brandGroup, brandGroupParams, [])

  return dispatch(updateFn(updatedParams, requestOptions))
}

export function useBrandGroupForm(
  brandGroup: Partial<BrandGroupModel> = {},
  options: UseFormOptions = {},
) {
  const { validateOn } = options

  const brandGroupForm = useForm(
    defaultState,
    { entity: brandGroup, requiredFields, validateOn },
    [brandGroup.id, brandGroup.cache_key],
  )

  return {
    ...brandGroupForm,
  }
}

export function useRelations(brandGroup: Partial<BrandGroupModel> = {}) {
  const { brand_id } = brandGroup

  const brands = useSelector(reduxState => reduxState.entities.brands)

  const brand: Partial<BrandModel> = brand_id ? brands[brand_id] || {} : {}

  return {
    brand,
  }
}

function useBrandGroup(initEntity: Partial<BrandGroupModel> = {}) {
  const { entity: brandGroup } = useLatestEntity(initEntity, 'brandGroups')
  const dispatch = useThunkDispatch()

  const { callbacks } = useContext(PageContext)

  const { creating, loading, updating } = useSelector(reduxState => reduxState.brandGroups)

  const { brand } = useRelations(brandGroup)

  return {
    brand,
    brandGroup,
    callbacks: {
      addBrandGroup: () => launchModal({
        callbacks,
        modalKey: 'CreateBrandGroupModal',
        payload: { brandGroup },
      }),
      createBrandGroup: (brandGroupParams: BrandGroupModel) => createBrandGroup({ brandGroupParams, dispatch }),
      updateBrandGroup: (brandGroupParams: BrandGroupModel, requestOptions: EntityOptions) => (
        updateBrandGroup({
          // @ts-expect-error TS2322
          brandGroup, brandGroupParams, dispatch, requestOptions,
        })
      ),
    },
    creating,
    loading,
    updating,
    urls: generateUrls(brandGroup),
  }
}

export default useBrandGroup
